import { useEffect, useMemo, useState } from 'react';
import type {
  LineMemberDto,
  ResearchLineDto,
  TeamDto,
  TeamMemberDto,
  TeamsOverviewDto,
} from '@ciimar/shared';
import { PortalModule } from '@ciimar/shared';
import { api } from '../api/client';
import { useAccess } from '../auth/AccessContext';
import { useT, type Translate } from '../lib/i18n';
import { matchesQuery } from '../lib/search';

function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={'team-chevron' + (open ? ' open' : '')}
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 8l5 5 5-5" />
    </svg>
  );
}

export function Teams() {
  const [data, setData] = useState<TeamsOverviewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'teams' | 'lines'>('teams');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const { canEdit } = useAccess();
  const editable = canEdit(PortalModule.Equipa);
  const t = useT();

  useEffect(() => {
    api
      .teamsOverview()
      .then(setData)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  async function sync() {
    setSyncing(true);
    setError(null);
    try {
      const res = await api.syncTeams();
      if (res.error) setError(t('teams.list.syncFailed', { error: res.error }));
      setData(await api.teamsOverview());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSyncing(false);
    }
  }

  function toggle(id: string) {
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  /** Switch to the Equipas tab, open that team and search for it. */
  function openTeam(code: string) {
    setTab('teams');
    setSearch(code);
    setOpen((s) => new Set(s).add('team:' + code));
  }

  const teams = useMemo(() => {
    const list = data?.teams ?? [];
    if (!search.trim()) return list;
    return list.filter((t) =>
      matchesQuery(
        [
          t.code,
          t.name,
          t.abbreviation,
          t.researchLineName,
          ...[...t.pis, ...t.copis, ...t.resources].map((m) => m.name),
        ]
          .filter(Boolean)
          .join(' '),
        search,
      ),
    );
  }, [data, search]);

  const lines = useMemo(() => {
    const list = data?.researchLines ?? [];
    if (!search.trim()) return list;
    return list.filter((l) =>
      matchesQuery(
        [l.code, l.name, l.abbreviation, ...[...l.pis, ...l.copis].map((m) => m.name)]
          .filter(Boolean)
          .join(' '),
        search,
      ),
    );
  }, [data, search]);

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('teams.list.title')}</h2>
          <p className="page-sub">
            {t('teams.list.subtitle')}
            {data &&
              ` · ${
                data.syncedAt
                  ? t('teams.list.syncedAt', { date: fmtDateTime(data.syncedAt) })
                  : t('teams.list.neverSynced')
              }`}
          </p>
        </div>
        <div className="spacer" />
        {data && (
          <span className={`badge ${data.source === 'none' ? 'changes' : 'approved'}`}>
            {t(`teams.list.source.${data.source}`)}
          </span>
        )}
        {editable && (
          <button
            type="button"
            className="secondary"
            onClick={() => void sync()}
            disabled={syncing}
          >
            {syncing ? t('teams.list.syncing') : t('teams.list.sync')}
          </button>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}
      {data?.error && (
        <div className="alert error">{t('teams.list.readFailed', { error: data.error })}</div>
      )}

      <div className="erp-filters" style={{ marginBottom: 16 }}>
        <div className="erp-seg erp-seg--stack">
          <button
            type="button"
            className={tab === 'teams' ? 'on' : ''}
            onClick={() => setTab('teams')}
          >
            {t('teams.list.title')} {data ? `(${data.teams.length})` : ''}
          </button>
          <button
            type="button"
            className={tab === 'lines' ? 'on' : ''}
            onClick={() => setTab('lines')}
          >
            {t('teams.list.linesTitle')} {data ? `(${data.researchLines.length})` : ''}
          </button>
        </div>
        <label className="erp-search">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14M21 21l-5-5" />
          </svg>
          <input
            placeholder={t('teams.list.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {loading ? (
        <p className="muted">{t('common.loading')}</p>
      ) : tab === 'teams' ? (
        teams.length === 0 ? (
          <div className="erp-panel">
            <div className="panel-empty is-empty">{t('teams.list.emptyTeams')}</div>
          </div>
        ) : (
          <div className="team-list">
            {teams.map((t) => (
              <TeamCard
                key={t.code}
                team={t}
                open={open.has('team:' + t.code)}
                onToggle={() => toggle('team:' + t.code)}
              />
            ))}
          </div>
        )
      ) : lines.length === 0 ? (
        <div className="erp-panel">
          <div className="panel-empty is-empty">{t('teams.list.emptyLines')}</div>
        </div>
      ) : (
        <div className="team-list">
          {lines.map((l) => (
            <LineCard
              key={l.code}
              line={l}
              teams={(data?.teams ?? []).filter((t) => teamBelongsToLine(t, l))}
              open={open.has('line:' + l.code)}
              onToggle={() => toggle('line:' + l.code)}
              onOpenTeam={openTeam}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Normaliza um código de linha temática para comparação robusta equipa↔linha:
 * ignora espaços, zeros à esquerda e maiúsculas/minúsculas, porque em modo SQL
 * a equipa pode trazer o código com formatação diferente da linha.
 */
function normCode(code: string | null | undefined): string {
  return (code ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/^0+(?=\d)/, '');
}

/** Normalize a name for comparison (case/space/accent-insensitive). */
function normName(name: string | null | undefined): string {
  return (name ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * Whether a team belongs to a thematic line. Matches by normalized code, and
 * falls back to the line name — so the grouping still works when the stored
 * team carries the line's name but not its code (e.g. data synced before the
 * `ResearchLine` code field was parsed).
 */
function teamBelongsToLine(t: TeamDto, l: ResearchLineDto): boolean {
  const tc = normCode(t.researchLineCode);
  if (tc && tc === normCode(l.code)) return true;
  const tn = normName(t.researchLineName);
  return tn !== '' && tn === normName(l.name);
}

/** One-line summary of responsáveis + member count (shown when collapsed). */
function teamSummary(team: TeamDto, t: Translate): string {
  const bits: string[] = [];
  if (team.pis.length)
    bits.push(t('teams.equipa.piNames', { names: team.pis.map((m) => m.name).join(', ') }));
  if (team.copis.length)
    bits.push(t('teams.equipa.copiNames', { names: team.copis.map((m) => m.name).join(', ') }));
  const total = team.pis.length + team.copis.length + team.resources.length;
  bits.push(total === 1 ? t('teams.memberOne') : t('teams.members', { n: total }));
  return bits.join(' · ');
}

function TeamCard({
  team,
  open,
  onToggle,
}: {
  team: TeamDto;
  open: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  return (
    <div className="erp-panel team-card">
      <button type="button" className="team-head" onClick={onToggle} aria-expanded={open}>
        <div className="team-head-main">
          <strong>{team.name}</strong>
          <span className="team-sub">
            {[team.code, team.abbreviation].filter(Boolean).join(' · ')}
          </span>
          {!open && <span className="team-summary">{teamSummary(team, t)}</span>}
        </div>
        {team.researchLineName && (
          <span className="badge" title={t('teams.equipa.lineBadge')}>
            {team.researchLineAbbreviation || team.researchLineName}
          </span>
        )}
        <Chevron open={open} />
      </button>
      {open && (
        <div className="team-body">
          <MemberGroup label="PI" members={team.pis} />
          <MemberGroup label="CO-PI" members={team.copis} />
          <MemberGroup label={t('teams.equipa.membersLabel')} members={team.resources} />
          {team.pis.length + team.copis.length + team.resources.length === 0 && (
            <p className="muted" style={{ margin: 0 }}>
              {t('teams.equipa.noMembers')}
            </p>
          )}
          {team.observations && <p className="team-obs">{team.observations}</p>}
        </div>
      )}
    </div>
  );
}

function MemberGroup({ label, members }: { label: string; members: TeamMemberDto[] }) {
  const t = useT();
  if (members.length === 0) return null;
  return (
    <div className="team-group">
      <div className="team-group-label">{label}</div>
      <ul className="team-members">
        {members.map((m) => {
          // These map to concepts the collaborator also carries on the ficha,
          // but here they describe the FCT research-team membership (for the
          // CIIMAR → FCT report), not day-to-day HR.
          const parts = [
            m.statusDescription
              ? { title: t('teams.equipa.roleInUnit'), text: m.statusDescription }
              : null,
            m.dedicationPercentage != null
              ? {
                  title: t('teams.equipa.dedicationTitle'),
                  text: t('teams.equipa.dedicationText', { n: m.dedicationPercentage }),
                }
              : null,
            m.entryDate
              ? {
                  title: t('teams.equipa.entryTitle'),
                  text: t('teams.equipa.entryText', { date: fmtDate(m.entryDate) }),
                }
              : null,
          ].filter((p): p is { title: string; text: string } => p != null);
          return (
            <li key={m.code + m.name} className="team-member">
              <span className="member-name">
                {m.name}
                {m.code && (
                  <span className="member-code" title={t('teams.equipa.memberCode')}>
                    #{m.code}
                  </span>
                )}
              </span>
              {parts.length > 0 && (
                <span className="member-meta">
                  {parts.map((p, i) => (
                    <span key={p.title} title={p.title}>
                      {i > 0 && <span className="member-sep"> · </span>}
                      {p.text}
                    </span>
                  ))}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function LineCard({
  line,
  teams,
  open,
  onToggle,
  onOpenTeam,
}: {
  line: ResearchLineDto;
  teams: TeamDto[];
  open: boolean;
  onToggle: () => void;
  onOpenTeam: (code: string) => void;
}) {
  const tr = useT();
  const summary = [
    teams.length
      ? teams.length === 1
        ? tr('teams.teamOne')
        : tr('teams.teamsCount', { n: teams.length })
      : null,
    line.pis.length
      ? tr('teams.equipa.piNames', { names: line.pis.map((m) => m.name).join(', ') })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="erp-panel team-card">
      <button type="button" className="team-head" onClick={onToggle} aria-expanded={open}>
        <div className="team-head-main">
          <strong>{line.name}</strong>
          <span className="team-sub">
            {[line.code, line.abbreviation].filter(Boolean).join(' · ')}
          </span>
          {!open && summary && <span className="team-summary">{summary}</span>}
        </div>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="team-body">
          <LineGroup label="PI" members={line.pis} />
          <LineGroup label="CO-PI" members={line.copis} />
          <div className="team-group">
            <div className="team-group-label">{tr('teams.equipa.lineTeams')}</div>
            {teams.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                {tr('teams.equipa.noTeams')}
              </p>
            ) : (
              <ul className="team-members">
                {teams.map((t) => (
                  <li key={t.code} className="line-team">
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onOpenTeam(t.code)}
                    >
                      {t.name}
                    </button>
                    <span className="member-meta">
                      {[
                        t.code,
                        (() => {
                          const n = t.pis.length + t.copis.length + t.resources.length;
                          return n
                            ? n === 1
                              ? tr('teams.memberOne')
                              : tr('teams.members', { n })
                            : null;
                        })(),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {line.observations && <p className="team-obs">{line.observations}</p>}
        </div>
      )}
    </div>
  );
}

function LineGroup({ label, members }: { label: string; members: LineMemberDto[] }) {
  if (members.length === 0) return null;
  return (
    <div className="team-group">
      <div className="team-group-label">{label}</div>
      <ul className="team-members">
        {members.map((m) => (
          <li key={m.code + m.name} className="team-member">
            <span className="member-name">{m.name}</span>
            {m.abbreviation && <span className="member-meta">{m.abbreviation}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
